import {
  collection, deleteDoc, doc, onSnapshot, serverTimestamp, setDoc, updateDoc,
} from 'firebase/firestore';
import { db } from './config';
import { HEARTBEAT_INTERVAL_MS, type QueuedStudent } from '../session/queue';

/**
 * Both queue timestamps are the SERVER's clock. They used to be Date.now()
 * from the device, which a student could simply choose: joinTime: 0 bought a
 * permanent front-of-queue (orderQueue sorts on it) and a post-dated
 * lastHeartbeat kept them in waitingCount forever with no app running. The
 * rules now require both fields to equal request.time, which serverTimestamp()
 * resolves to, so neither is a number the client gets to pick.
 *
 * DEFERRED — three unsynchronised clocks. Fixing the WRITE side does not
 * make the app single-clocked: isStale() below still compares this
 * server-written heartbeat against the reader's own Date.now(), so a student's
 * device and the معلم's device can still disagree about who is present, and
 * the sweeper's clock is a third. Reconciling all three is out of scope here.
 */
function toMillis(value: unknown): number {
  if (typeof value === 'number') return value;
  if (value && typeof (value as { toMillis?: unknown }).toMillis === 'function') {
    return (value as { toMillis: () => number }).toMillis();
  }
  return 0;
}

export async function joinQueue(
  sessionId: string, studentId: string, name: string
): Promise<() => void> {
  const ref = doc(db, 'waitingRooms', sessionId, 'students', studentId);
  await setDoc(ref, {
    name, joinTime: serverTimestamp(), lastHeartbeat: serverTimestamp(),
  });

  const timer = setInterval(() => {
    void updateDoc(ref, { lastHeartbeat: serverTimestamp() }).catch(() => {
      // A failed beat is not fatal: the student simply ages out and rejoins.
    });
  }, HEARTBEAT_INTERVAL_MS);

  return () => {
    clearInterval(timer);
    void deleteDoc(ref).catch(() => {
      // Best effort. If this never lands the stale threshold removes them.
    });
  };
}

export function listenToQueue(
  sessionId: string, cb: (students: QueuedStudent[]) => void
): () => void {
  return onSnapshot(collection(db, 'waitingRooms', sessionId, 'students'), (snap) => {
    cb(snap.docs.map((d) => {
      // 'estimate' fills a server timestamp that has not been acknowledged yet
      // with a local guess. Without it the writer's own pending beat reads as
      // null, normalises to 0, and they watch themselves drop out of the queue
      // they just joined.
      const data = d.data({ serverTimestamps: 'estimate' }) as Record<string, unknown>;
      return {
        studentId: d.id,
        name: String(data.name ?? ''),
        joinTime: toMillis(data.joinTime),
        lastHeartbeat: toMillis(data.lastHeartbeat),
      };
    }));
  });
}
